import React, { useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { CircleHelp, Plus, Trash2 } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import TimeSelect from 'src/components/property-touring-hours/TimeSelect';
import {
    type SpecialDayForm,
    defaultWindow,
    getWindowsError,
} from 'src/components/property-touring-hours/touring-hours-form';
import classes from 'src/components/property-touring-hours/PropertyTouringHoursSection.module.css';

type Props = {
    specialDays: SpecialDayForm[];
    isReadOnly: boolean;
    onUpdate: (index: number, update: (day: SpecialDayForm) => SpecialDayForm) => void;
    onToggle: (index: number, isOpen: boolean) => void;
    onDelete: (index: number) => void;
    onAdd: (date: string) => void;
};

export default function SpecialDaysSection({
    specialDays,
    isReadOnly,
    onUpdate,
    onToggle,
    onDelete,
    onAdd,
}: Props) {
    const [isAddingSpecialDate, setAddingSpecialDate] = useState(false);

    return (
        <FlexColumn rowGap={1}>
            <div className={classes.specialTitle}>
                <Typography variant="body2" fontWeight={500}>Date-specific hours</Typography>
                <Tooltip title="Use date-specific hours for holidays or one-off activities. These hours apply only to the selected date and do not repeat weekly.">
                    <IconButton className={classes.helpIcon} size="small" data-testid="date-specific-hours-help-button">
                        <CircleHelp size={16} />
                    </IconButton>
                </Tooltip>
            </div>
            {specialDays.map((specialDay, index) => {
                const windowsError = specialDay.is_closed ? null : getWindowsError(specialDay.windows);
                return (
                    <div className={`${classes.dayRow} ${classes.specialRow}`} key={`${specialDay.date}-${index}`}>
                        <div className={classes.specialPrefix}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color={specialDay.is_closed ? 'text.secondary' : 'text.primary'}
                            >
                                {specialDay.date}
                            </Typography>
                            <Switch
                                checked={!specialDay.is_closed}
                                onChange={(_, checked) => onToggle(index, checked)}
                                size="small"
                                disabled={isReadOnly}
                                inputProps={{ 'aria-label': `Open ${specialDay.date}` }}
                                data-testid="toggle-tour-availability-special-day-switch"
                            />
                            {!isReadOnly && (
                                <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => onDelete(index)}
                                    data-testid="delete-tour-availability-special-day-button"
                                >
                                    <Trash2 size={16} />
                                </IconButton>
                            )}
                        </div>
                        <div className={classes.windowList}>
                            {specialDay.is_closed ? (
                                <div className={classes.emptyWindowRow}>
                                    <Typography variant="body2" color="text.secondary">Closed</Typography>
                                </div>
                            ) : (
                                <>
                                    {specialDay.windows.map((window, windowIndex) => (
                                        <div className={classes.windowRow} key={windowIndex}>
                                            <TimeSelect
                                                value={window.open_time}
                                                onChange={(time) => onUpdate(index, (day) => ({
                                                    ...day,
                                                    windows: day.windows.map((currentWindow, j) => j === windowIndex ? { ...currentWindow, open_time: time } : currentWindow),
                                                }))}
                                                testId={`tour-availability-special-open-${windowIndex}`}
                                                disabled={isReadOnly}
                                            />
                                            <TimeSelect
                                                value={window.close_time}
                                                onChange={(time) => onUpdate(index, (day) => ({
                                                    ...day,
                                                    windows: day.windows.map((currentWindow, j) => j === windowIndex ? { ...currentWindow, close_time: time } : currentWindow),
                                                }))}
                                                testId={`tour-availability-special-close-${windowIndex}`}
                                                disabled={isReadOnly}
                                            />
                                            {!isReadOnly && (
                                                <div className={classes.windowActions}>
                                                    <IconButton
                                                        type="button"
                                                        size="small"
                                                        onClick={() => onUpdate(index, (day) => ({
                                                            ...day,
                                                            windows: [...day.windows, defaultWindow()],
                                                        }))}
                                                        data-testid="add-tour-availability-special-window-button"
                                                    >
                                                        <Plus size={16} />
                                                    </IconButton>
                                                    <IconButton
                                                        type="button"
                                                        size="small"
                                                        onClick={() => onUpdate(index, (day) => ({
                                                            ...day,
                                                            windows: day.windows.filter((_, j) => j !== windowIndex),
                                                        }))}
                                                        data-testid="delete-tour-availability-special-window-button"
                                                    >
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {specialDay.windows.length === 0 && (
                                        <div className={classes.emptyWindowRow}>
                                            <Typography variant="body2" color="text.secondary">No hours set</Typography>
                                            {!isReadOnly && (
                                                <IconButton
                                                    type="button"
                                                    size="small"
                                                    onClick={() => onUpdate(index, (day) => ({
                                                        ...day,
                                                        windows: [...day.windows, defaultWindow()],
                                                    }))}
                                                    data-testid="add-tour-availability-special-window-button"
                                                >
                                                    <Plus size={16} />
                                                </IconButton>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            {windowsError && (
                                <Typography variant="caption" color="error" data-testid="tour-availability-special-error">
                                    {windowsError}
                                </Typography>
                            )}
                        </div>
                    </div>
                );
            })}

            {!isReadOnly && (isAddingSpecialDate ? (
                <DatePicker
                    open
                    value={null}
                    onClose={() => setAddingSpecialDate(false)}
                    shouldDisableDate={(day) =>
                        specialDays.some((specialDay) => specialDay.date === day.format('YYYY-MM-DD'))
                    }
                    onChange={(value) => {
                        if (value) {
                            onAdd(value.format('YYYY-MM-DD'));
                        }
                        setAddingSpecialDate(false);
                    }}
                    slotProps={{
                        textField: {
                            size: 'small',
                            fullWidth: true,
                            inputProps: {
                                'data-testid': 'tour-availability-special-date-picker',
                            },
                        },
                    }}
                />
            ) : (
                <Button
                    type="button"
                    variant="outlined"
                    className={classes.addButton}
                    onClick={() => setAddingSpecialDate(true)}
                    data-testid="add-tour-availability-special-day-button"
                >
                    + Add Date-Specific Hours
                </Button>
            ))}
        </FlexColumn>
    );
}
